import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CapitalBookSummaryV2, PortfolioStatementImportV2, StatementEvidenceLocatorV2,
  StatementImportIntentV2, StatementImportReceiptV2, StatementImportReviewDecisionV2 } from "@tradvera/contracts";
import type { MyPortfolioNextAccount } from "../client.js";
import { createStatementImportClientV2, statementImportWriteOutcomeUnknownV2,
  type StatementImportCapabilitiesV2 } from "./client";
import { executeDurableStatementUploadV2, type DurableStatementUploadStorageV2,
  type StatementUploadRecoveryV2 } from "./durable-upload";
import { recoverPendingStatementWriteV2, type StatementWriteRecoveryV2 } from "./durable-write";
import { readPendingStatementOperationV2, type PendingStatementOperationV2 } from "./pending-operation";
import { findStatementUploadQueuePendingV2, retireMissingPendingUploadForNewBundleV2,
  type StatementUploadQueueItemV2 } from "./pending-upload-alignment";
import { statementUploadChoiceForFileV2, type StatementUploadChoiceV2 } from "./upload";
import { executeRecoverableStatementUploadFileV2, rememberUploadedImportV2,
  type StatementImportUploadAttemptV2 } from "./recoverable-upload";
import { confirmStatementImportFromWorkspaceV2, createStatementIntentFromWorkspaceV2,
  discardStatementImportFromWorkspaceV2, reconcileStatementImportFromWorkspaceV2,
  reviewStatementImportFromWorkspaceV2 } from "./workspace-write-actions";
import { statementImportPriorityV2, statementSmartFileSummaryV2,
  statementSmartReviewDecisionsV2 } from "../domain/statement-smart-summary.js";

export type StatementImportWorkspaceBusyV2 = "LOADING" | "CAPABILITIES" | "HASHING_UPLOAD" | "DETAIL" | "RECOVERING"
  | "REVIEW" | "RECONCILIATION" | "ACCEPT_INTENT" | "ACCEPT_CONFIRM" | "DISCARD" | null;
export type StatementImportWorkspaceErrorSourceV2 = "READ" | "UPLOAD" | "RECOVERY" | "DETAIL" | "WORKFLOW" | "EVIDENCE" | null;

export interface StatementImportWorkspaceControllerV2 {
  imports: PortfolioStatementImportV2[]; selected: PortfolioStatementImportV2 | null;
  lastUploadBatch: PortfolioStatementImportV2[];
  busy: StatementImportWorkspaceBusyV2; errorCode: string | null;
  errorSource: StatementImportWorkspaceErrorSourceV2; recovery: StatementUploadRecoveryV2;
  capabilities: StatementImportCapabilitiesV2 | null; capabilityError: string | null;
  recoveredIntent: StatementImportIntentV2 | null; recoveredReceipt: StatementImportReceiptV2 | null;
  upload(files: readonly File[]): Promise<StatementImportUploadAttemptV2>;
  approveLastUploadBatch(): Promise<boolean>;
  select(importId: string): Promise<void>; refresh(): void; recover(): Promise<StatementUploadRecoveryV2>;
  review(decisions: StatementImportReviewDecisionV2[], expectedRevision: number): Promise<void>;
  reconcile(): Promise<void>; createIntent(imported: PortfolioStatementImportV2): Promise<StatementImportIntentV2>;
  confirm(imported: PortfolioStatementImportV2, intent: StatementImportIntentV2): Promise<StatementImportReceiptV2>;
  discard(imported: PortfolioStatementImportV2): Promise<void>;
  openEvidence(locator: StatementEvidenceLocatorV2): Promise<Blob>;
}

export function useStatementImportWorkspaceV2(options: {
  account: MyPortfolioNextAccount; book: CapitalBookSummaryV2; booksEndpoint: string;
  storage: DurableStatementUploadStorageV2; adminSession?: string; onAdminSessionInvalid?(): void;
}): StatementImportWorkspaceControllerV2 {
  const writable = options.book.status === "ACTIVE";
  const scope = `${options.account.userId}\u0000${options.book.bookId}\u0000${options.booksEndpoint}\u0000${options.book.status}`;
  const latestScope = useRef(scope); latestScope.current = scope;
  const selectionEpoch = useRef(0); const initializedScope = useRef<string | null>(null);
  const client = useMemo(() => createStatementImportClientV2({ account: options.account,
    booksEndpoint: options.booksEndpoint, bookId: options.book.bookId, bookKind: options.book.kind,
    adminSession: options.adminSession }),
  [options.account, options.adminSession, options.book.bookId, options.book.kind, options.booksEndpoint]);
  const [imports, setImports] = useState<PortfolioStatementImportV2[]>([]);
  const [selected, setSelected] = useState<PortfolioStatementImportV2 | null>(null);
  const [lastUploadBatch, setLastUploadBatch] = useState<PortfolioStatementImportV2[]>([]);
  const selectedRef = useRef(selected); selectedRef.current = selected;
  const [busy, setBusy] = useState<StatementImportWorkspaceBusyV2>("LOADING");
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorSource, setErrorSource] = useState<StatementImportWorkspaceErrorSourceV2>(null);
  const [recovery, setRecovery] = useState<StatementUploadRecoveryV2>({ state: "ABSENT" });
  const [capabilities, setCapabilities] = useState<StatementImportCapabilitiesV2 | null>(null);
  const [capabilityError, setCapabilityError] = useState<string | null>(null);
  const capabilityEpoch = useRef(0);
  const [intentTruth, setIntentTruth] = useState<StatementImportIntentV2 | null>(null);
  const [receiptTruth, setReceiptTruth] = useState<StatementImportReceiptV2 | null>(null);
  const [reload, setReload] = useState(0);

  const applyImport = useCallback((imported: PortfolioStatementImportV2, epoch: number) => {
    if (scope !== latestScope.current || epoch !== selectionEpoch.current
      || (selectedRef.current && selectedRef.current.importId !== imported.importId)) return false;
    setSelected(imported); return true;
  }, [scope]);

  const recoverTruth = useCallback(async (signal?: AbortSignal): Promise<StatementWriteRecoveryV2> => {
    const epoch = selectionEpoch.current;
    const result = await recoverPendingStatementWriteV2({ client, storage: options.storage,
      ownerId: options.account.userId, bookId: options.book.bookId,
      expectedBookKind: options.book.kind, signal });
    if (scope !== latestScope.current || signal?.aborted) return result;
    const mapped = uploadRecovery(result); setRecovery(mapped);
    if (result.state === "RECOVERED" && applyImport(result.import, epoch)) {
      if (result.intent) setIntentTruth(result.intent);
      if (result.receipt) setReceiptTruth(result.receipt);
    }
    if (result.state === "LOCKED") {
      if (adminSessionInvalidCode(result.code)) options.onAdminSessionInvalid?.();
      setErrorCode(result.code); setErrorSource("RECOVERY");
    }
    return result;
  }, [applyImport, client, options.account.userId, options.book.bookId, options.storage, scope]);

  const load = useCallback(async (signal?: AbortSignal) => {
    const requestedScope = scope; setBusy("LOADING"); setErrorCode(null); setErrorSource(null);
    try {
      const result = await client.list(signal);
      if (requestedScope !== latestScope.current || signal?.aborted) return;
      setImports(result); setSelected((current) => current
        ? result.find((item) => item.importId === current.importId) ?? null : null); setBusy(null);
    } catch (error) {
      if (requestedScope !== latestScope.current || signal?.aborted) return;
      invalidateSession(error, options.onAdminSessionInvalid);
      setImports([]); setBusy(null); setErrorCode(codeOf(error)); setErrorSource("READ");
    }
  }, [client, scope]);

  useEffect(() => {
    const controller = new AbortController();
    if (initializedScope.current !== scope) {
      initializedScope.current = scope; selectionEpoch.current += 1;
      setImports([]); setSelected(null); setLastUploadBatch([]); setRecovery({ state: "ABSENT" });
      setIntentTruth(null); setReceiptTruth(null); setCapabilities(null); setCapabilityError(null);
    }
    void (async () => {
      setBusy("RECOVERING"); await recoverTruth(controller.signal);
      if (!controller.signal.aborted) await load(controller.signal);
    })();
    if (writable) {
      const requestEpoch = ++capabilityEpoch.current;
      setCapabilities(null); setCapabilityError(null);
      void client.capabilities(controller.signal).then((truth) => {
        if (scope === latestScope.current && !controller.signal.aborted
          && requestEpoch === capabilityEpoch.current) {
          setCapabilities(truth); setCapabilityError(null);
        }
      }).catch((error) => {
        if (scope !== latestScope.current || controller.signal.aborted
          || requestEpoch !== capabilityEpoch.current) return;
        invalidateSession(error, options.onAdminSessionInvalid); setCapabilityError(codeOf(error));
      });
    }
    return () => controller.abort();
  }, [client, load, recoverTruth, reload, scope, writable]);

  const recover = useCallback(async () => {
    setBusy("RECOVERING"); setErrorCode(null); setErrorSource(null);
    const result = await recoverTruth(); if (scope === latestScope.current) setBusy(null);
    return uploadRecovery(result);
  }, [recoverTruth, scope]);

  const upload = useCallback(async (files: readonly File[]): Promise<StatementImportUploadAttemptV2> => {
    if (!writable) {
      return uploadFailure("STATEMENT_IMPORT_BOOK_NOT_ACTIVE", setBusy, setErrorCode, setErrorSource);
    }
    let activeCapabilities = capabilities;
    if (!activeCapabilities) {
      const requestEpoch = ++capabilityEpoch.current;
      setBusy("CAPABILITIES"); clearError(setErrorCode, setErrorSource); setCapabilityError(null);
      try {
        activeCapabilities = await resolveStatementImportCapabilitiesV2(null, () => client.capabilities());
        if (scope !== latestScope.current) return { ok: false, errorCode: "STATEMENT_IMPORT_SCOPE_CHANGED" };
        // A concurrent capability probe may supersede this state write, but its valid server
        // result remains sufficient for the upload that requested it. Never cancel the upload.
        if (requestEpoch === capabilityEpoch.current) {
          setCapabilities(activeCapabilities); setCapabilityError(null);
        }
      } catch (error) {
        if (scope !== latestScope.current) return { ok: false, errorCode: "STATEMENT_IMPORT_SCOPE_CHANGED" };
        invalidateSession(error, options.onAdminSessionInvalid);
        const code = codeOf(error); setBusy(null); setCapabilityError(code);
        setErrorCode(code); setErrorSource("UPLOAD"); return { ok: false, errorCode: code };
      }
    }
    if (files.length < 1 || files.length > 100) {
      return uploadFailure("STATEMENT_IMPORT_FILE_COUNT_INVALID", setBusy, setErrorCode, setErrorSource);
    }
    let queued: readonly StatementUploadQueueItemV2[];
    try {
      queued = files.map((file) => ({ file, choice: statementUploadChoiceForFileV2(file) }));
    } catch (error) {
      return uploadFailure(codeOf(error), setBusy, setErrorCode, setErrorSource);
    }
    const invalid = queued.find(({ file, choice }) => file.size > activeCapabilities.maxUploadBytes
      || file.size < 1 || !formatAvailable(choice, activeCapabilities));
    if (invalid) { const code = invalid.file.size > activeCapabilities.maxUploadBytes
      ? "STATEMENT_IMPORT_FILE_TOO_LARGE" : invalid.file.size < 1
        ? "STATEMENT_IMPORT_FILE_EMPTY" : "STATEMENT_IMPORT_FORMAT_UNSUPPORTED";
      return uploadFailure(code, setBusy, setErrorCode, setErrorSource); }
    setBusy("HASHING_UPLOAD"); clearError(setErrorCode, setErrorSource);
    const uploaded: PortfolioStatementImportV2[] = [];
    try {
      const pending = readPendingStatementOperationV2(options.storage,
        options.account.userId, options.book.bookId);
      if (pending.state === "INVALID" || pending.state === "UNAVAILABLE") {
        throw new Error(`STATEMENT_IMPORT_PENDING_OPERATION_${pending.state}`);
      }
      if (pending.state === "VALID") {
        const truth = await recoverTruth();
        if (truth.state === "LOCKED") throw new Error(truth.code);
        if (truth.state === "PENDING") throw new Error("STATEMENT_IMPORT_PENDING_OPERATION_PENDING");
        if (truth.state === "RETRY_ALLOWED") {
          if (truth.marker.operation !== "UPLOAD") {
            throw new Error("STATEMENT_IMPORT_PENDING_OPERATION_OTHER_OPERATION");
          }
          const aligned = await findStatementUploadQueuePendingV2(
            queued, options.book.bookId, truth.marker);
          if (aligned) queued = [aligned.pending, ...aligned.remaining];
          else {
            await retireMissingPendingUploadForNewBundleV2({ storage: options.storage,
              ownerId: options.account.userId, marker: truth.marker,
              confirmStatus: () => client.operationStatus({ operation: "UPLOAD", importId: null,
                idempotencyKey: truth.marker.idempotencyKey,
                inputFingerprint: truth.marker.inputFingerprint, dispositionCounts: null }) });
          }
        } else if (truth.state === "RECOVERED" && truth.marker.operation === "UPLOAD") {
          const aligned = await findStatementUploadQueuePendingV2(
            queued, options.book.bookId, truth.marker);
          if (aligned) { rememberUploadedImportV2(uploaded, truth.import); queued = aligned.remaining; }
        }
      }
      for (const { file, choice } of queued) {
        const imported = await executeRecoverableStatementUploadFileV2({
          execute: async () => (await executeDurableStatementUploadV2({ client, storage: options.storage,
            ownerId: options.account.userId, bookId: options.book.bookId, file, choice })).import,
          recover: recoverTruth,
          onFailure: (error) => invalidateSession(error, options.onAdminSessionInvalid),
        });
        rememberUploadedImportV2(uploaded, imported);
      }
      if (scope !== latestScope.current) return { ok: false, errorCode: "STATEMENT_IMPORT_SCOPE_CHANGED" };
      const latest = bestStatementImportV2(uploaded);
      selectionEpoch.current += 1; setLastUploadBatch(uploaded); setSelected(latest); setRecovery({ state: "ABSENT" });
      setBusy(null); setReload((value) => value + 1); return { ok: true };
    } catch (error) {
      const code = codeOf(error);
      if (scope !== latestScope.current) return { ok: false, errorCode: "STATEMENT_IMPORT_SCOPE_CHANGED" };
      invalidateSession(error, options.onAdminSessionInvalid);
      if (uploaded.length > 0) {
        setLastUploadBatch([...uploaded]); setSelected(bestStatementImportV2(uploaded));
      }
      return uploadFailure(code, setBusy, setErrorCode, setErrorSource);
    }
  }, [capabilities, client, options.account.userId, options.book.bookId,
    options.storage, recoverTruth, scope, writable]);

  const approveLastUploadBatch = useCallback(async () => {
    if (!writable || lastUploadBatch.length === 0) return false;
    const epoch = selectionEpoch.current;
    setBusy("REVIEW"); clearError(setErrorCode, setErrorSource);
    const updated: PortfolioStatementImportV2[] = [];
    try {
      for (const original of lastUploadBatch) {
        const summary = statementSmartFileSummaryV2(original);
        if (summary.actionableRecords === 0 || ["OBSERVATIONS_ACCEPTED", "DUPLICATE_NOOP", "ABANDONED"]
          .includes(original.state)) { updated.push(original); continue; }
        let truth = original;
        if (["REVIEW_REQUIRED", "BLOCKED", "READY_TO_CONFIRM", "INTENT_CREATED", "RECONCILING"]
          .includes(truth.state)) {
          truth = await reviewStatementImportFromWorkspaceV2(actionScope(truth),
            statementSmartReviewDecisionsV2(truth), truth.reviewRevision);
        }
        if (truth.state === "RECONCILING") {
          truth = await reconcileStatementImportFromWorkspaceV2(actionScope(truth));
        }
        if (truth.state === "READY_TO_CONFIRM") {
          const intent = await createStatementIntentFromWorkspaceV2(actionScope(truth));
          await confirmStatementImportFromWorkspaceV2(actionScope(truth), intent);
          truth = await client.detail(truth.importId);
        }
        updated.push(truth);
      }
      if (scope !== latestScope.current || epoch !== selectionEpoch.current) return false;
      setLastUploadBatch(updated); setSelected(bestStatementImportV2(updated)); setBusy(null);
      setReload((value) => value + 1); return true;
    } catch (error) {
      if (scope !== latestScope.current || epoch !== selectionEpoch.current) return false;
      if (updated.length > 0) { setLastUploadBatch([...updated, ...lastUploadBatch.slice(updated.length)]);
        setSelected(bestStatementImportV2(updated)); }
      invalidateSession(error, options.onAdminSessionInvalid);
      setBusy(null); setErrorCode(codeOf(error)); setErrorSource("WORKFLOW"); setReload((value) => value + 1);
      return false;
    }
  }, [client, lastUploadBatch, options.account.userId, options.book.bookId, options.storage, scope, writable]);

  const select = useCallback(async (importId: string) => {
    const requestedScope = scope; const epoch = ++selectionEpoch.current;
    setIntentTruth(null); setReceiptTruth(null); setBusy("DETAIL"); clearError(setErrorCode, setErrorSource);
    try {
      const detail = await client.detail(importId);
      if (requestedScope !== latestScope.current || epoch !== selectionEpoch.current) return;
      setSelected(detail); setBusy(null);
    } catch (error) {
      if (requestedScope !== latestScope.current || epoch !== selectionEpoch.current) return;
      invalidateSession(error, options.onAdminSessionInvalid);
      setBusy(null); setErrorCode(codeOf(error)); setErrorSource("DETAIL");
    }
  }, [client, scope]);

  const review = useCallback(async (decisions: StatementImportReviewDecisionV2[], expectedRevision: number) => {
    const imported = requireSelected(selectedRef.current, writable); const epoch = selectionEpoch.current;
    setBusy("REVIEW"); clearError(setErrorCode, setErrorSource);
    try { const truth = await reviewStatementImportFromWorkspaceV2(actionScope(imported), decisions, expectedRevision);
      if (applyImport(truth, epoch)) setBusy(null); }
    catch (error) { if (await recoveredOperation(error, "REVIEW", epoch)) return;
      workflowFailure(error, epoch); throw error; }
  }, [applyImport, client, options.account.userId, options.book.bookId, options.storage, recoverTruth, writable]);

  const reconcile = useCallback(async () => {
    const imported = requireSelected(selectedRef.current, writable); const epoch = selectionEpoch.current;
    setBusy("RECONCILIATION"); clearError(setErrorCode, setErrorSource);
    try { if (applyImport(await reconcileStatementImportFromWorkspaceV2(actionScope(imported)), epoch)) setBusy(null); }
    catch (error) { if (await recoveredOperation(error, "RECONCILIATION", epoch)) return;
      workflowFailure(error, epoch); throw error; }
  }, [applyImport, client, options.account.userId, options.book.bookId, options.storage, recoverTruth, writable]);

  const createIntent = useCallback(async (imported: PortfolioStatementImportV2) => {
    requireCurrent(imported, selectedRef.current, writable); const epoch = selectionEpoch.current;
    setBusy("ACCEPT_INTENT"); clearError(setErrorCode, setErrorSource);
    try {
      const intent = await createStatementIntentFromWorkspaceV2(actionScope(imported));
      if (epoch === selectionEpoch.current && selectedRef.current?.importId === imported.importId) {
        setIntentTruth(intent); await refreshDetail(imported.importId, epoch); setBusy(null);
      }
      return intent;
    } catch (error) {
      const truth = await recoverOnFailure(error);
      if (truth?.state === "RECOVERED" && truth.marker.operation === "ACCEPT_INTENT" && truth.intent) {
        if (epoch === selectionEpoch.current) setBusy(null); return truth.intent;
      }
      workflowFailure(error, epoch); throw error;
    }
  }, [client, options.account.userId, options.book.bookId, options.storage, recoverTruth, writable]);

  const confirm = useCallback(async (imported: PortfolioStatementImportV2, intent: StatementImportIntentV2) => {
    requireCurrent(imported, selectedRef.current, writable); const epoch = selectionEpoch.current;
    setBusy("ACCEPT_CONFIRM"); clearError(setErrorCode, setErrorSource);
    try {
      const receipt = await confirmStatementImportFromWorkspaceV2(actionScope(imported), intent);
      if (epoch === selectionEpoch.current && selectedRef.current?.importId === imported.importId) {
        setReceiptTruth(receipt); await refreshDetail(imported.importId, epoch); setBusy(null);
      }
      return receipt;
    } catch (error) {
      const truth = await recoverOnFailure(error);
      if (truth?.state === "RECOVERED" && truth.marker.operation === "ACCEPT_CONFIRM" && truth.receipt) {
        if (epoch === selectionEpoch.current) setBusy(null); return truth.receipt;
      }
      workflowFailure(error, epoch); throw error;
    }
  }, [client, options.account.userId, options.book.bookId, options.storage, recoverTruth, writable]);

  const discard = useCallback(async (imported: PortfolioStatementImportV2) => {
    requireCurrent(imported, selectedRef.current, writable); const epoch = selectionEpoch.current;
    setBusy("DISCARD"); clearError(setErrorCode, setErrorSource);
    try { if (applyImport(await discardStatementImportFromWorkspaceV2(actionScope(imported)), epoch)) setBusy(null); }
    catch (error) { if (await recoveredOperation(error, "DISCARD", epoch)) return;
      workflowFailure(error, epoch); throw error; }
  }, [applyImport, client, options.account.userId, options.book.bookId, options.storage, recoverTruth, writable]);

  const openEvidence = useCallback(async (locator: StatementEvidenceLocatorV2) => {
    const imported = requireSelected(selectedRef.current, true); const epoch = selectionEpoch.current;
    try {
      const blob = await client.evidence({ importId: imported.importId, evidenceToken: locator.textDigest,
        mediaType: imported.file.mediaType, bytes: imported.file.bytes, fileSha256: imported.file.sha256 });
      if (scope !== latestScope.current || epoch !== selectionEpoch.current
        || selectedRef.current?.importId !== imported.importId) throw new Error("STATEMENT_IMPORT_SELECTION_CHANGED");
      return blob;
    } catch (error) {
      invalidateSession(error, options.onAdminSessionInvalid);
      if (epoch === selectionEpoch.current) { setErrorCode(codeOf(error)); setErrorSource("EVIDENCE"); }
      throw error;
    }
  }, [client, scope]);

  return { imports, selected, lastUploadBatch, busy, errorCode, errorSource, recovery, capabilities, capabilityError,
    recoveredIntent: intentTruth?.importId === selected?.importId ? intentTruth : null,
    recoveredReceipt: receiptTruth?.importId === selected?.importId ? receiptTruth : null,
    upload, approveLastUploadBatch, select, refresh: () => setReload((value) => value + 1), recover,
    review, reconcile, createIntent, confirm, discard, openEvidence };

  function actionScope(imported: PortfolioStatementImportV2) { return { client, storage: options.storage,
    ownerId: options.account.userId, bookId: options.book.bookId, imported }; }
  async function refreshDetail(importId: string, epoch: number) {
    try { applyImport(await client.detail(importId), epoch); }
    catch (error) { invalidateSession(error, options.onAdminSessionInvalid);
      setErrorCode(codeOf(error)); setErrorSource("DETAIL"); }
  }
  async function recoverOnFailure(error: unknown) {
    invalidateSession(error, options.onAdminSessionInvalid);
    return shouldRecover(error) ? recoverTruth() : null;
  }
  async function recoveredOperation(error: unknown, operation: PendingStatementOperationV2["operation"], epoch: number) {
    const truth = await recoverOnFailure(error);
    if (truth?.state === "RECOVERED" && truth.marker.operation === operation) {
      if (applyImport(truth.import, epoch)) setBusy(null); return true;
    }
    return false;
  }
  function workflowFailure(error: unknown, epoch: number) {
    if (epoch !== selectionEpoch.current) return;
    setBusy(null); setErrorCode(codeOf(error)); setErrorSource("WORKFLOW");
  }
}

/** A click must be able to finish capability discovery instead of silently returning. */
export async function resolveStatementImportCapabilitiesV2(
  current: StatementImportCapabilitiesV2 | null,
  load: () => Promise<StatementImportCapabilitiesV2>,
): Promise<StatementImportCapabilitiesV2> {
  return current ?? load();
}
function uploadFailure(code: string,
  setBusy: (value: StatementImportWorkspaceBusyV2) => void,
  setCode: (value: string | null) => void,
  setSource: (value: StatementImportWorkspaceErrorSourceV2) => void): StatementImportUploadAttemptV2 {
  setBusy(null); setCode(code); setSource("UPLOAD");
  return { ok: false, errorCode: code };
}
function bestStatementImportV2(imports: readonly PortfolioStatementImportV2[]): PortfolioStatementImportV2 | null {
  return [...imports].sort((left, right) => statementImportPriorityV2(right) - statementImportPriorityV2(left))[0] ?? null;
}
function uploadRecovery(result: StatementWriteRecoveryV2): StatementUploadRecoveryV2 {
  if (result.state === "ABSENT") return result;
  if (result.state === "LOCKED") return { state: "LOCKED", code: result.code };
  if (result.state === "RECOVERED") return { state: "RECOVERED", import: result.import, status: result.status };
  if (result.marker.operation !== "UPLOAD") return { state: "LOCKED",
    code: result.state === "RETRY_ALLOWED" ? "STATEMENT_IMPORT_EXACT_OPERATION_RETRY_REQUIRED" : "PENDING" };
  const marker = { operation: "UPLOAD" as const, idempotencyKey: result.marker.idempotencyKey,
    inputFingerprint: result.marker.inputFingerprint, bookId: result.marker.bookId };
  return result.state === "RETRY_ALLOWED" ? { state: "RETRY_EXACT_FILE", marker, status: result.status }
    : { state: "PENDING", marker, status: result.status };
}
function requireSelected(value: PortfolioStatementImportV2 | null, writable: boolean) {
  if (!value || !writable) throw new Error("STATEMENT_IMPORT_BOOK_NOT_ACTIVE"); return value;
}
function requireCurrent(expected: PortfolioStatementImportV2, current: PortfolioStatementImportV2 | null, writable: boolean) {
  if (!writable || !current || expected.importId !== current.importId || expected.updatedAt !== current.updatedAt) {
    throw new Error("STATEMENT_IMPORT_SELECTION_CHANGED");
  }
}
function formatAvailable(choice: StatementUploadChoiceV2, truth: StatementImportCapabilitiesV2) {
  const required = choice === "CSV" ? ["parser.csv"] : choice === "OFX" || choice === "QFX"
    ? ["parser.ofx-qfx"] : choice === "PDF" ? ["parser.pdf-native", "parser.pdf-scanned"] : ["parser.image-ocr"];
  return required.every((id) => truth.capabilities.some((item) => item.id === id && item.available));
}
function shouldRecover(error: unknown) { return statementImportWriteOutcomeUnknownV2(error)
  || /PENDING_OPERATION/u.test(error instanceof Error ? error.message : ""); }
function adminSessionInvalidCode(code: string) { return ["ADMIN_SESSION_REQUIRED", "ADMIN_SESSION_EXPIRED",
  "ADMIN_STEP_UP_NOT_FRESH", "PIN_CHANGE_REQUIRED"].includes(code); }
function invalidateSession(error: unknown, callback?: () => void) {
  if (error instanceof Error && adminSessionInvalidCode(error.message)) callback?.();
}
function codeOf(error: unknown): string { return statementImportWriteOutcomeUnknownV2(error)
  ? "STATEMENT_IMPORT_OUTCOME_UNKNOWN" : error instanceof Error && error.message
    ? error.message : "STATEMENT_IMPORT_RESPONSE_INVALID"; }
function clearError(setCode: (value: string | null) => void,
  setSource: (value: StatementImportWorkspaceErrorSourceV2) => void) { setCode(null); setSource(null); }

export { executeRecoverableStatementUploadFileV2, shouldRecoverStatementUploadFailureV2 } from "./recoverable-upload";
